import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Control, useFieldArray } from 'react-hook-form';

import { Field, FormElement as FormElementDefinition, StepInstanceId, Temporal } from '@willow/types-iso';

import { assertNever } from '../../utils';
import {
  ControlledBorrowerMultiSelect,
  ControlledBorrowerSelect,
  ControlledCheckbox,
  ControlledInput,
  ControlledRadio,
  ControlledSelect,
  ControlledSimpleSelect,
  ControlledSingleDatePicker,
} from '../FormElements';
import { RenderedMarkdown } from './RenderedMarkdown';
import { useWorkflowLoanContext } from './WorkflowLoanContext';

export interface FormElementProps {
  fieldName: string;
  element: FormElementDefinition;
  control: Control<Record<string, any>>;
  disabled: boolean;
  stepInstanceId: StepInstanceId;

  // not sure why this prop is triggering the linter because it's definitely being used...
  // eslint-disable-next-line react/no-unused-prop-types
  hideLabel?: true;
}

export const FormElement = ({
  fieldName,
  element,
  control,
  disabled,
  stepInstanceId,
  hideLabel,
  className,
}: FormElementProps & { className?: string }) => {
  return (
    <div className={className}>
      <FormElementElement
        fieldName={fieldName}
        element={element}
        control={control}
        disabled={disabled}
        stepInstanceId={stepInstanceId}
        hideLabel={hideLabel}
      />
    </div>
  );
};

const FormElementElement = ({ fieldName, element, control, disabled, stepInstanceId, hideLabel }: FormElementProps) => {
  const { borrowersById } = useWorkflowLoanContext();
  const borrowers = useMemo(() => Array.from(borrowersById.values()), [borrowersById]);

  if (element.elementType === 'presentationOnlyMarkdown') {
    return (
      <div className="u-fs-2">
        <RenderedMarkdown markdownContent={element.markdownContent} />
      </div>
    );
  }

  if (element.elementType === 'empty') {
    return null;
  }

  if (element.multiValued) {
    return (
      <MultiValuedFormElement
        fieldName={fieldName}
        element={element}
        control={control}
        disabled={disabled}
        stepInstanceId={stepInstanceId}
      />
    );
  }

  switch (element.elementType) {
    case 'textInput':
      return (
        <ControlledInput
          fieldName={fieldName}
          label={!hideLabel ? element.label : undefined}
          control={control}
          required={element.required}
          disabled={disabled}
        />
      );
    case 'select':
      return element.typeaheadSearch ? (
        <ControlledSelect
          fieldName={fieldName}
          label={!hideLabel ? element.label : undefined}
          control={control}
          options={element.validValues.map((value) => ({ value, label: value }))}
          required={element.required}
          disabled={disabled}
        />
      ) : (
        <ControlledSimpleSelect
          fieldName={fieldName}
          label={!hideLabel ? element.label : undefined}
          control={control}
          options={[{ value: '', label: 'Select an option' }].concat(
            element.validValues.map((value) => ({ value, label: value })),
          )} // TODO labels
          required={element.required}
          disabled={disabled}
        />
      );
    case 'radio':
      return (
        <ControlledRadio
          fieldName={fieldName}
          label={!hideLabel ? element.label : undefined}
          control={control}
          options={element.validValues.map((value) => ({ value, label: value }))} // TODO labels
          required={element.required}
        />
      );
    case 'boolean':
      switch (element.presentation) {
        case 'checkbox':
          // TODO how to handle required?
          return <ControlledCheckbox fieldName={fieldName} label={element.label} control={control} />;
        case 'radio':
          return (
            <ControlledRadio
              fieldName={fieldName}
              label={!hideLabel ? element.label : undefined}
              control={control}
              options={[
                { value: 'true', label: element.trueLabel ?? 'True' },
                { value: 'false', label: element.falseLabel ?? 'False' },
              ]}
              optionIdPrefix={`${stepInstanceId}-${fieldName}`}
              required={element.required}
            />
          );
        default:
          return assertNever(element.presentation);
      }
    case 'textArea':
      return (
        <ControlledInput
          control={control}
          fieldName={fieldName}
          inputType="textarea"
          label={!hideLabel ? element.label : undefined}
          required={element.required}
          disabled={disabled}
        />
      );
    case 'numberInput': // TODO
    case 'currencyInput':
      return (
        <ControlledInput
          control={control}
          fieldName={fieldName}
          inputType="number"
          // min={element.min}
          // max={element.max}
          label={!hideLabel ? element.label : undefined}
          required={element.required}
          disabled={disabled}
        />
      );
    case 'plainDate':
      return (
        <ControlledSingleDatePicker
          control={control}
          fieldName={fieldName}
          label={!hideLabel ? element.label : undefined}
          placeholder="Select date"
          className="u-fs-2"
          disabled={disabled}
          required={element.required}
        />
      );
    case 'email':
      return (
        <ControlledInput
          control={control}
          fieldName={fieldName}
          inputType="email"
          label={!hideLabel ? element.label : undefined}
          required={element.required}
          disabled={disabled}
        />
      );
    case 'phoneNumber':
      return (
        <ControlledInput
          control={control}
          fieldName={fieldName}
          inputType="tel"
          label={!hideLabel ? element.label : undefined}
          required={element.required}
          disabled={disabled}
        />
      );
    case 'group':
      return (
        <Form.Group className="u-border-color-willow border rounded p-4">
          {!hideLabel && <Form.Label>{element.label}</Form.Label>}
          <div>
            {element.elements.map(({ key, element }) => (
              <FormElement
                key={key}
                fieldName={`${fieldName}.${key}`}
                element={element}
                control={control}
                disabled={disabled}
                stepInstanceId={stepInstanceId}
              />
            ))}
          </div>
        </Form.Group>
      );
    case 'borrowerSelect':
      return (
        <ControlledBorrowerSelect
          fieldName={fieldName}
          label={!hideLabel ? element.label : undefined}
          control={control}
          borrowers={borrowers}
          required={element.required}
          disabled={disabled}
        />
      );
    default:
      return assertNever(element);
  }
};

const MultiValuedFormElement = ({ fieldName, element, control, disabled, stepInstanceId }: FormElementProps) => {
  const { borrowersById } = useWorkflowLoanContext();
  const borrowers = useMemo(() => Array.from(borrowersById.values()), [borrowersById]);

  const { fields, append, remove } = useFieldArray({
    name: fieldName,
    control,
  });

  if (element.elementType === 'presentationOnlyMarkdown' || element.elementType === 'empty') {
    throw new Error(`${element.elementType} cannot be multi-valued`);
  }

  switch (element.elementType) {
    case 'select':
      return (
        <ControlledSelect
          isMulti
          fieldName={fieldName}
          label={element.label}
          control={control}
          options={element.validValues.map((value) => ({ value, label: value }))}
          required={element.required}
        />
      );
    case 'borrowerSelect':
      return (
        <ControlledBorrowerMultiSelect
          borrowers={borrowers}
          fieldName={fieldName}
          label={element.label}
          control={control}
          disabled={disabled}
          required={element.required}
        />
      );
    case 'textInput':
    case 'email':
    case 'phoneNumber':
    case 'currencyInput':
    case 'numberInput':
    case 'plainDate':
    case 'radio':
    case 'textArea':
    case 'boolean':
    case 'group':
      return (
        <div>
          <div className="d-flex align-items-baseline justify-content-between mb-2">
            <Form.Label className="u-fs-2 fw-bold form-label">{element.label}</Form.Label>
            <Button
              variant="link"
              onClick={() => append(getEmptyValueForSchemaType(element.schemaType))}
              className="u-fs-2"
            >
              <PlusOutlined /> Add Value
            </Button>
          </div>
          {fields.map((field, index) => (
            <div key={field.id} className="d-flex align-items-start justify-content-between mb-2">
              <FormElement
                fieldName={`${fieldName}.${index}`}
                element={{ ...element, multiValued: false }}
                control={control}
                disabled={disabled}
                stepInstanceId={stepInstanceId}
                hideLabel
                className="flex-grow-1"
              />
              <Button variant="link" className="u-fs-2" onClick={() => remove(index)}>
                <DeleteOutlined />
              </Button>
            </div>
          ))}
        </div>
      );
    default:
      return assertNever(element);
  }
};

function getEmptyValueForSchemaType(schemaType: Field['schemaType']) {
  switch (schemaType) {
    case 'email':
    case 'phoneNumber':
    case 'string':
      return '';
    case 'object':
      return {};
    case 'boolean':
      return false;
    case 'number':
    case 'currency':
      return 0;
    case 'plainDate':
      return Temporal.Now.plainDateISO();
    case 'borrower':
      return undefined;
    default:
      return assertNever(schemaType);
  }
}
